import { useMemo, useCallback } from 'react';
import { DataGrid, type GridColDef, type GridPaginationModel, type GridSortModel, type GridRenderCellParams } from '@mui/x-data-grid';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import type { ColumnMeta } from '../../types/reportBuilder';
import { evaluateFormat, type CellStyle } from '../../utils/conditionalFormatUtils';
import ChartView from './ChartView';
import PivotView from './PivotTable';
import { FIELD_COLORS } from './fieldEncoding';

function formatCellValue(value: unknown, col: ColumnMeta): string {
  if (value === null || value === undefined) return '-';
  switch (col.display_format) {
    case 'percentage': return `${Number(value).toFixed(1)}%`;
    case 'currency': return `$${Number(value).toLocaleString()}`;
    case 'decimal_2': return Number(value).toFixed(2);
    case 'date_short': return value ? new Date(String(value)).toLocaleDateString() : '-';
    case 'datetime': return value ? new Date(String(value)).toLocaleString() : '-';
    default: return String(value);
  }
}

export default function QueryResults() {
  const { results, queryLoading, viewMode, limit, offset, sorts, conditionalFormats, setOffset, setLimit, addSort, removeSort, executeQuery } = useReportBuilderStore();

  // Pre-compute column numeric values for conditional formatting
  const columnValues = useMemo(() => {
    if (!results?.data || conditionalFormats.length === 0) return {};
    const vals: Record<string, number[]> = {};
    for (const rule of conditionalFormats) {
      if (!vals[rule.column]) {
        vals[rule.column] = results.data
          .map(row => Number(row[rule.column]))
          .filter(v => !isNaN(v));
      }
    }
    return vals;
  }, [results?.data, conditionalFormats]);

  // Build a map of column -> format rules for quick lookup
  const formatRulesByColumn = useMemo(() => {
    const map: Record<string, typeof conditionalFormats> = {};
    for (const rule of conditionalFormats) {
      if (!map[rule.column]) map[rule.column] = [];
      map[rule.column].push(rule);
    }
    return map;
  }, [conditionalFormats]);

  const gridColumns: GridColDef[] = useMemo(() => {
    if (!results?.columns) return [];
    return results.columns.map((col) => {
      const colRules = formatRulesByColumn[col.key];
      const colDef: GridColDef = {
        field: col.key, headerName: col.label, flex: 1, minWidth: 120, sortable: col.sortable,
        headerClassName: col.is_dimension ? 'rb-dim-header' : col.is_measure ? 'rb-meas-header' : '',
        valueFormatter: (value: unknown) => formatCellValue(value, col),
      };

      // Apply conditional formatting via renderCell when rules exist for this column
      if (colRules && colRules.length > 0) {
        colDef.renderCell = (params: GridRenderCellParams) => {
          const allVals = columnValues[col.key] ?? [];
          let mergedStyle: CellStyle = {};
          for (const rule of colRules) {
            const style = evaluateFormat(params.value, rule, allVals);
            mergedStyle = { ...mergedStyle, ...style };
          }
          return (
            <div className="flex h-full w-full items-center px-2" style={mergedStyle}>
              {formatCellValue(params.value, col)}
            </div>
          );
        };
      }

      return colDef;
    });
  }, [results?.columns, formatRulesByColumn, columnValues]);

  const rows = useMemo(() => {
    if (!results?.data) return [];
    return results.data.map((row, idx) => ({ id: idx, ...row }));
  }, [results?.data]);

  const rowCount = results?.pagination?.total ?? results?.query_stats?.row_count ?? 0;
  const paginationModel: GridPaginationModel = useMemo(() => ({
    page: limit > 0 ? Math.floor(offset / limit) : 0, pageSize: limit,
  }), [offset, limit]);

  const handlePaginationChange = useCallback((model: GridPaginationModel) => {
    setLimit(model.pageSize); setOffset(model.page * model.pageSize); executeQuery();
  }, [setLimit, setOffset, executeQuery]);

  const sortModel: GridSortModel = useMemo(() => sorts.map(s => ({ field: s.column, sort: s.direction as 'asc' | 'desc' })), [sorts]);

  const handleSortChange = useCallback((model: GridSortModel) => {
    for (const s of sorts) removeSort(s.id);
    for (const item of model) { if (item.field && item.sort) addSort({ column: item.field, direction: item.sort }); }
    executeQuery();
  }, [sorts, removeSort, addSort, executeQuery]);

  if (!results && !queryLoading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white">
        <div className="text-center">
          <p className="text-sm text-gray-400">Click fields in the Data Pane to build your query</p>
          <p className="mt-1 text-xs text-gray-300">Right-click fields for more options (filter, sort, time grouping)</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {viewMode === 'chart' && results && <ChartView />}
      {viewMode === 'pivot' && results && <PivotView />}

      {viewMode === 'table' && (
        <div className="h-[calc(100vh-280px)] min-h-[300px] w-full">
          <DataGrid rows={rows} columns={gridColumns} loading={queryLoading}
            paginationMode="server" sortingMode="server" rowCount={rowCount}
            paginationModel={paginationModel} onPaginationModelChange={handlePaginationChange}
            sortModel={sortModel} onSortModelChange={handleSortChange}
            pageSizeOptions={[25, 50, 100, 250]} disableRowSelectionOnClick
            sx={{
              [`& .rb-dim-header`]: { backgroundColor: FIELD_COLORS.dimension.headerBg },
              [`& .rb-meas-header`]: { backgroundColor: FIELD_COLORS.measure.headerBg },
              border: '1px solid #e5e7eb', borderRadius: '0.5rem',
              backgroundColor: '#fff',
            }}
          />
        </div>
      )}
    </div>
  );
}
